/**
 * GymRequestCard Component
 *
 * Displays a summary card for a gym request with status badge.
 * Used in the user's gym requests list and admin dashboard.
 */

import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { GymRequest } from '@flash/shared';
import { GymRequestStatusBadge } from './GymRequestStatusBadge';

/**
 * Props for GymRequestCard component
 */
export interface GymRequestCardProps {
  /** The gym request to display */
  request: GymRequest;
  /** Link destination (defaults to detail page) */
  linkTo?: string;
  /** Additional CSS styles */
  style?: CSSProperties;
}

/**
 * Format a timestamp for display
 */
const formatDate = (timestamp: unknown): string => {
  if (!timestamp) {
    return 'Unknown date';
  }

  // Handle Firestore Timestamp
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    const date = (timestamp as { toDate: () => Date }).toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  return 'Unknown date';
};

/**
 * GymRequestCard displays a summary view of a gym request
 *
 * @example
 * ```tsx
 * <GymRequestCard request={gymRequest} />
 * <GymRequestCard request={gymRequest} linkTo={`/admin/requests/${gymRequest.id}`} />
 * ```
 */
export const GymRequestCard = ({ request, linkTo, style }: GymRequestCardProps) => {
  const defaultLink = `/gym-requests/${request.id}`;
  const destination = linkTo ?? defaultLink;

  return (
    <Link to={destination} style={{ textDecoration: 'none' }}>
      <div style={{ ...cardStyle, ...style }}>
        {/* Status Badge */}
        <div style={headerStyle}>
          <GymRequestStatusBadge status={request.status} />
        </div>

        {/* Gym Info */}
        <div style={contentStyle}>
          <h3 style={nameStyle}>{request.name}</h3>
          <p style={locationStyle}>
            {request.city}, {request.state}, {request.country}
          </p>
          <p style={dateStyle}>Submitted: {formatDate(request.createdAt)}</p>
        </div>

        {/* Admin Notes Indicator */}
        {request.adminNotes && (
          <div style={notesIndicatorStyle}>
            <span style={notesIconStyle}>!</span>
            <span>Admin feedback available</span>
          </div>
        )}
      </div>
    </Link>
  );
};

/**
 * Component styles
 */
const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '20px',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: '1px solid #e5e7eb',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '12px',
};

const contentStyle: CSSProperties = {
  flex: 1,
};

const nameStyle: CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1f2937',
};

const locationStyle: CSSProperties = {
  margin: '0 0 4px 0',
  fontSize: '0.875rem',
  color: '#6b7280',
};

const dateStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: '#9ca3af',
};

const notesIndicatorStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '12px',
  padding: '8px 12px',
  backgroundColor: '#fef3c7',
  borderRadius: '6px',
  fontSize: '0.75rem',
  color: '#92400e',
};

const notesIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '18px',
  height: '18px',
  backgroundColor: '#f59e0b',
  color: 'white',
  borderRadius: '50%',
  fontWeight: 700,
  fontSize: '0.75rem',
};

export default GymRequestCard;
